import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createUfoDecipherRound,
  isUfoAnswerCorrect,
  UFO_DECIPHER_TIME_LIMIT_SEC,
  UFO_VICTORY_HOLD_MS,
} from '../../../../domain';
import {
  UFO_BUBBLE_URL,
  UFO_CHARACTER_URL,
  UFO_QUIZ_BAD_URL,
  UFO_QUIZ_GOOD_URL,
  UFO_QUIZ_NEUTRAL_URL,
  UFO_TILE_URL,
  UFO_TIMER_URL,
  UFO_VICTORY_URL,
} from '../assets';
import './UfoDecipherGame.css';

export type UfoDecipherGameProps = {
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'play' | 'victory';
type Feedback = 'good' | 'bad' | null;

const BUTTON_LAYOUT = [
  { left: 195, top: 1129.12 },
  { left: 195, top: 1249.25 },
  { left: 195, top: 1369.39 },
] as const;

const FEEDBACK_HOLD_MS = 450;

function formatUfoTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

function quizSkinUrl(feedback: Feedback, index: number, selectedIndex: number | null): string {
  if (selectedIndex === null || index !== selectedIndex) {
    return UFO_QUIZ_NEUTRAL_URL;
  }
  if (feedback === 'good') {
    return UFO_QUIZ_GOOD_URL;
  }
  if (feedback === 'bad') {
    return UFO_QUIZ_BAD_URL;
  }
  return UFO_QUIZ_NEUTRAL_URL;
}

export function UfoDecipherGame({
  onWin,
  onLose,
  onTimerChange,
}: UfoDecipherGameProps) {
  const round = useMemo(() => createUfoDecipherRound(), []);
  const [secondsLeft, setSecondsLeft] = useState<number>(UFO_DECIPHER_TIME_LIMIT_SEC);
  const [phase, setPhase] = useState<Phase>('play');
  const [finished, setFinished] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    if (phase !== 'play' || finished) {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    const timer = window.setTimeout(() => {
      onWin();
    }, UFO_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  const pickOption = useCallback(
    (index: number) => {
      if (finished || phase !== 'play' || locked) {
        return;
      }
      setLocked(true);
      setSelectedIndex(index);
      const correct = isUfoAnswerCorrect(index, round.correctIndex);
      setFeedback(correct ? 'good' : 'bad');

      window.setTimeout(() => {
        setFinished(true);
        if (correct) {
          setPhase('victory');
          return;
        }
        onLose();
      }, FEEDBACK_HOLD_MS);
    },
    [finished, locked, onLose, phase, round.correctIndex],
  );

  const controlsDisabled = finished || locked || phase !== 'play';

  return (
    <div className="ufo-decipher">
      <img className="ufo-decipher__tile" src={UFO_TILE_URL} alt="" draggable={false} />
      <h2 className="ufo-decipher__title">Переведи слова</h2>
      <p className="ufo-decipher__hint">Что говорит Уфо?</p>

      <img
        className="ufo-decipher__character"
        src={UFO_CHARACTER_URL}
        alt=""
        draggable={false}
      />
      <img className="ufo-decipher__bubble" src={UFO_BUBBLE_URL} alt="" draggable={false} />
      <p className="ufo-decipher__phrase" aria-live="polite">
        {round.phrase}
      </p>

      {BUTTON_LAYOUT.map((slot, index) => (
        <button
          key={`ufo-option-${index}`}
          type="button"
          className={`ufo-decipher__option${selectedIndex === index ? ' is-selected' : ''}`}
          style={{ left: slot.left, top: slot.top }}
          onClick={() => pickOption(index)}
          disabled={controlsDisabled}
        >
          <img
            className="ufo-decipher__option-art"
            src={quizSkinUrl(feedback, index, selectedIndex)}
            alt=""
            draggable={false}
          />
          <span className="ufo-decipher__option-label">{round.options[index]}</span>
        </button>
      ))}

      <div className="ufo-decipher__timer">
        <img className="ufo-decipher__timer-art" src={UFO_TIMER_URL} alt="" draggable={false} />
        <p className="ufo-decipher__timer-value">{formatUfoTimer(secondsLeft)}</p>
      </div>

      {phase === 'victory' ? (
        <div className="ufo-decipher__victory" aria-live="polite">
          <img
            className="ufo-decipher__victory-art"
            src={UFO_VICTORY_URL}
            alt=""
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
